import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SdrGoalsRepository,
  describeGoalsApiError,
  getSdrGoal,
  type SdrGoalUser,
} from '../../data/repositories';
import {
  HabitaButton,
  HabitaCheckbox,
  HabitaEmptyState,
  HabitaErrorNotice,
  HabitaSelect,
  HabitaTextInput,
  classNames,
  habitaSurfaceClass,
} from '../../shared/components/habita';
import {
  GOAL_TYPE_DEFINITIONS,
  type GoalType,
  type GoalsCompetencyConfiguration,
} from '../../shared/models';
import { getAuthProfiles } from '../../shared/utils';
import {
  buildReferenceMonthOptions,
  currentReferenceMonth,
  formatMonthLabel,
} from '../../shared/utils/monthOptions';

const monthLabels = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

type GoalDraft = Record<GoalType, string>;
type UserGoalDrafts = Record<string, GoalDraft>;

interface CompetencyBaseline {
  enabledTypes: GoalType[];
  teamGoals: GoalDraft;
  workingDays: string;
}

interface UserGoalChange {
  goal: number;
  type: GoalType;
  userId: string;
  userName: string;
}

function goalDraft(initialValue = ''): GoalDraft {
  return Object.fromEntries(
    GOAL_TYPE_DEFINITIONS.map(({ type }) => [type, initialValue])
  ) as GoalDraft;
}

function normalizeGoalInput(value: string): string {
  return value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
}

function monthEndDate(referenceMonth: string): Date {
  const [year, month] = referenceMonth.split('-').map(Number);
  return new Date(year, month, 0, 23, 59, 59, 999);
}

function isAvailableForReferenceMonth(user: SdrGoalUser, referenceMonth: string): boolean {
  if (user.profile !== 'SDR' || user.status !== 'ACTIVE') return false;
  if (!user.created_at) return true;

  const createdAt = new Date(user.created_at);
  return Number.isNaN(createdAt.getTime()) || createdAt <= monthEndDate(referenceMonth);
}

function previousReferenceMonth(referenceMonth: string): string {
  const [year, month] = referenceMonth.split('-').map(Number);
  const previous = new Date(year, month - 2, 1);
  return `${previous.getFullYear()}-${String(previous.getMonth() + 1).padStart(2, '0')}`;
}

function sortedTypes(types: GoalType[]): GoalType[] {
  return GOAL_TYPE_DEFINITIONS
    .map(({ type }) => type)
    .filter((type) => types.includes(type));
}

function sameTypes(left: GoalType[], right: GoalType[]): boolean {
  const sortedLeft = sortedTypes(left);
  const sortedRight = sortedTypes(right);
  return (
    sortedLeft.length === sortedRight.length &&
    sortedLeft.every((type, index) => type === sortedRight[index])
  );
}

function teamGoalDraft(configuration: GoalsCompetencyConfiguration): GoalDraft {
  const draft = goalDraft();
  configuration.team_goals.forEach(({ goal, type }) => {
    draft[type] = String(goal);
  });
  return draft;
}

function userGoalDrafts(users: SdrGoalUser[], referenceMonth: string): UserGoalDrafts {
  return Object.fromEntries(users.map((user) => {
    const draft = goalDraft();
    GOAL_TYPE_DEFINITIONS.forEach(({ type }) => {
      const goal = getSdrGoal(user.goals, referenceMonth, type);
      draft[type] = goal === null ? '' : String(goal);
    });
    return [user.id, draft];
  }));
}

function cloneUserGoalDrafts(drafts: UserGoalDrafts): UserGoalDrafts {
  return Object.fromEntries(
    Object.entries(drafts).map(([userId, draft]) => [userId, { ...draft }])
  );
}

function countWeekdays(referenceMonth: string): number {
  const [year, month] = referenceMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  let weekdays = 0;

  for (let day = 1; day <= lastDay; day += 1) {
    const weekDay = new Date(year, month - 1, day).getDay();
    if (weekDay !== 0 && weekDay !== 6) weekdays += 1;
  }

  return weekdays;
}

export default function Parametros() {
  const goalsRepository = useMemo(() => new SdrGoalsRepository(), []);
  const loadRequestRef = useRef(0);
  const canEdit = useMemo(
    () => getAuthProfiles().some((profile) => ['ADMIN', 'SUPERVISOR'].includes(profile)),
    []
  );

  const [referenceMonth, setReferenceMonth] = useState(currentReferenceMonth());
  const [users, setUsers] = useState<SdrGoalUser[]>([]);
  const [workingDays, setWorkingDays] = useState('');
  const [enabledTypes, setEnabledTypes] = useState<GoalType[]>([]);
  const [teamGoals, setTeamGoals] = useState<GoalDraft>(() => goalDraft());
  const [userGoals, setUserGoals] = useState<UserGoalDrafts>({});
  const [competencyBaseline, setCompetencyBaseline] = useState<CompetencyBaseline | null>(null);
  const [userGoalsBaseline, setUserGoalsBaseline] = useState<UserGoalDrafts>({});
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [savingCompetency, setSavingCompetency] = useState(false);
  const [savingUsers, setSavingUsers] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadData = useCallback(async (period: string) => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;

    try {
      setLoading(true);
      setLoadError('');
      setActionError('');
      setSuccessMessage('');

      const [configuration, nextUsers] = await Promise.all([
        goalsRepository.fetchCompetency(period),
        goalsRepository.fetchSdrGoalUsers(),
      ]);
      if (requestId !== loadRequestRef.current) return;

      const nextEnabledTypes = configuration.goal_types
        .filter(({ enabled }) => enabled)
        .map(({ type }) => type);
      const nextTeamGoals = teamGoalDraft(configuration);
      const nextUserGoals = userGoalDrafts(nextUsers, period);
      const nextWorkingDays = String(configuration.competency.working_days);

      setUsers(nextUsers);
      setWorkingDays(nextWorkingDays);
      setEnabledTypes(nextEnabledTypes);
      setTeamGoals(nextTeamGoals);
      setUserGoals(nextUserGoals);
      setCompetencyBaseline({
        enabledTypes: nextEnabledTypes,
        teamGoals: { ...nextTeamGoals },
        workingDays: nextWorkingDays,
      });
      setUserGoalsBaseline(cloneUserGoalDrafts(nextUserGoals));
    } catch (error) {
      if (requestId !== loadRequestRef.current) return;
      setUsers([]);
      setCompetencyBaseline(null);
      setUserGoalsBaseline({});
      setLoadError(describeGoalsApiError(
        error,
        `Não foi possível carregar a configuração de ${formatMonthLabel(period)}.`
      ));
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  }, [goalsRepository]);

  useEffect(() => {
    void loadData(referenceMonth);
  }, [loadData, referenceMonth]);

  const activeSdrs = useMemo(() => (
    users
      .filter((user) => isAvailableForReferenceMonth(user, referenceMonth))
      .sort((left, right) => (
        left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' }) ||
        left.email.localeCompare(right.email, 'pt-BR', { sensitivity: 'base' })
      ))
  ), [referenceMonth, users]);

  const enabledGoalDefinitions = useMemo(() => (
    GOAL_TYPE_DEFINITIONS.filter(({ type }) => enabledTypes.includes(type))
  ), [enabledTypes]);

  const competencyChanged = useMemo(() => {
    if (!competencyBaseline) return false;
    if (workingDays !== competencyBaseline.workingDays) return true;
    if (!sameTypes(enabledTypes, competencyBaseline.enabledTypes)) return true;

    return enabledTypes.some((type) => teamGoals[type] !== competencyBaseline.teamGoals[type]);
  }, [competencyBaseline, enabledTypes, teamGoals, workingDays]);

  const userGoalChanges = useMemo(() => {
    const changes: UserGoalChange[] = [];
    activeSdrs.forEach((user) => {
      enabledTypes.forEach((type) => {
        const draftValue = userGoals[user.id]?.[type] ?? '';
        const baselineValue = userGoalsBaseline[user.id]?.[type] ?? '';
        if (draftValue !== baselineValue && draftValue !== '') {
          changes.push({
            goal: Number(draftValue),
            type,
            userId: user.id,
            userName: user.name,
          });
        }
      });
    });
    return changes;
  }, [activeSdrs, enabledTypes, userGoals, userGoalsBaseline]);

  const clearedUserGoals = useMemo(() => (
    activeSdrs.flatMap((user) => enabledTypes
      .filter((type) => (
        (userGoalsBaseline[user.id]?.[type] ?? '') !== '' &&
        (userGoals[user.id]?.[type] ?? '') === ''
      ))
      .map((type) => ({ type, userName: user.name }))
    )
  ), [activeSdrs, enabledTypes, userGoals, userGoalsBaseline]);

  const unavailableUserGoalChanges = useMemo(() => {
    const persistedEnabledTypes = competencyBaseline?.enabledTypes ?? [];
    return userGoalChanges.filter(({ type }) => !persistedEnabledTypes.includes(type));
  }, [competencyBaseline, userGoalChanges]);

  const referenceMonthOptions = useMemo(
    () => buildReferenceMonthOptions(referenceMonth),
    [referenceMonth]
  );
  const yearOptions = useMemo(() => (
    Array.from(new Set(referenceMonthOptions.map((value) => value.slice(0, 4))))
      .sort()
      .map((year) => ({ label: year, value: year }))
  ), [referenceMonthOptions]);
  const [selectedYear, selectedMonth] = referenceMonth.split('-');

  function clearMessages() {
    setActionError('');
    setSuccessMessage('');
  }

  function updateReferenceMonth(year: string, month: string) {
    setReferenceMonth(`${year}-${month}`);
  }

  function toggleGoalType(type: GoalType, enabled: boolean) {
    clearMessages();
    setEnabledTypes((current) => (
      enabled
        ? sortedTypes([...current, type])
        : current.filter((item) => item !== type)
    ));
  }

  async function copyPreviousMonth() {
    const previousMonth = previousReferenceMonth(referenceMonth);
    try {
      setCopying(true);
      clearMessages();
      const previousConfiguration = await goalsRepository.fetchCompetency(previousMonth);
      const previousEnabledTypes = previousConfiguration.goal_types
        .filter(({ enabled }) => enabled)
        .map(({ type }) => type);
      const previousTeamGoals = teamGoalDraft(previousConfiguration);
      const previousUserGoals = userGoalDrafts(users, previousMonth);

      setWorkingDays(String(previousConfiguration.competency.working_days));
      setEnabledTypes(previousEnabledTypes);
      setTeamGoals(previousTeamGoals);
      setUserGoals((current) => {
        const copiedDrafts = cloneUserGoalDrafts(current);
        users.forEach((user) => {
          copiedDrafts[user.id] = {
            ...goalDraft(),
            ...previousUserGoals[user.id],
          };
        });
        return copiedDrafts;
      });
      setSuccessMessage(
        `Configuração de ${formatMonthLabel(previousMonth)} copiada para o rascunho. Salve para aplicar.`
      );
    } catch (error) {
      setActionError(describeGoalsApiError(
        error,
        `Não foi possível consultar ${formatMonthLabel(previousMonth)} para copiar as metas.`
      ));
    } finally {
      setCopying(false);
    }
  }

  async function saveCompetency() {
    const parsedWorkingDays = Number(workingDays);
    if (!Number.isInteger(parsedWorkingDays) || parsedWorkingDays < 1 || parsedWorkingDays > 31) {
      setActionError('Informe uma quantidade de dias úteis entre 1 e 31.');
      return;
    }

    const missingTeamGoal = enabledTypes.find((type) => teamGoals[type] === '');
    if (missingTeamGoal) {
      const label = GOAL_TYPE_DEFINITIONS.find(({ type }) => type === missingTeamGoal)?.label;
      setActionError(`Informe a meta do time para ${label ?? missingTeamGoal}.`);
      return;
    }

    try {
      setSavingCompetency(true);
      clearMessages();
      await goalsRepository.configureCompetency(referenceMonth, {
        enabled_types: sortedTypes(enabledTypes),
        working_days: parsedWorkingDays,
      });

      const results = await Promise.allSettled(enabledTypes.map((type) => (
        goalsRepository.updateTeamGoal(referenceMonth, {
          goal: Number(teamGoals[type]),
          type,
        })
      )));
      const failedResult = results.find((result) => result.status === 'rejected');
      if (failedResult?.status === 'rejected') {
        await loadData(referenceMonth);
        setActionError(describeGoalsApiError(
          failedResult.reason,
          'A competência foi salva, mas uma ou mais metas do time não foram atualizadas.'
        ));
        return;
      }

      await loadData(referenceMonth);
      setSuccessMessage('Competência e metas do time atualizadas com sucesso.');
    } catch (error) {
      setActionError(describeGoalsApiError(
        error,
        'Não foi possível salvar a competência e as metas do time.'
      ));
    } finally {
      setSavingCompetency(false);
    }
  }

  async function saveUserGoals() {
    if (clearedUserGoals.length > 0) {
      setActionError(
        'A API não possui ação para excluir uma meta. Para remover seu valor efetivo, informe 0.'
      );
      return;
    }
    if (userGoalChanges.length === 0) return;

    try {
      setSavingUsers(true);
      clearMessages();
      const results = await Promise.allSettled(userGoalChanges.map((change) => (
        goalsRepository.updateUserGoal(change.userId, {
          goal: change.goal,
          period: referenceMonth,
          type: change.type,
        })
      )));

      const successfulChanges = userGoalChanges.filter((_, index) => results[index].status === 'fulfilled');
      const failedResults = results.filter((result) => result.status === 'rejected');
      let unconfirmedChanges: UserGoalChange[] = [];

      if (successfulChanges.length > 0) {
        const refreshedUsers = await goalsRepository.fetchSdrGoalUsers();
        unconfirmedChanges = successfulChanges.filter((change) => {
          const refreshedUser = refreshedUsers.find((user) => user.id === change.userId);
          return (
            !refreshedUser
            || getSdrGoal(refreshedUser.goals, referenceMonth, change.type) !== change.goal
          );
        });
        const refreshedUserGoals = userGoalDrafts(refreshedUsers, referenceMonth);

        setUsers(refreshedUsers);
        setUserGoals(refreshedUserGoals);
        setUserGoalsBaseline(cloneUserGoalDrafts(refreshedUserGoals));
      }

      if (unconfirmedChanges.length > 0) {
        const affectedUsers = Array.from(new Set(
          unconfirmedChanges.map(({ userName }) => userName)
        )).join(', ');
        setActionError(
          `A API respondeu ao salvamento, mas a consulta ainda retornou o valor anterior para: ${affectedUsers}. ` +
          'A alteração não foi considerada persistida.'
        );
        return;
      }

      if (failedResults.length > 0) {
        const firstFailure = failedResults[0];
        setActionError(describeGoalsApiError(
          firstFailure.status === 'rejected' ? firstFailure.reason : null,
          successfulChanges.length > 0
            ? `${successfulChanges.length} meta(s) foram salvas, mas ${failedResults.length} falharam.`
            : 'Não foi possível salvar as metas dos SDRs.'
        ));
        return;
      }

      setSuccessMessage(
        userGoalChanges.length === 1
          ? 'Meta do SDR atualizada com sucesso.'
          : `${userGoalChanges.length} metas de SDR atualizadas com sucesso.`
      );
    } catch (error) {
      setActionError(describeGoalsApiError(
        error,
        'Não foi possível confirmar a persistência das metas dos SDRs.'
      ));
    } finally {
      setSavingUsers(false);
    }
  }

  return (
    <section className="mx-auto w-full max-w-[1240px] space-y-5">
      <div>
        <h1 className="font-fraunces text-2xl font-semibold text-textPrimary">Parâmetros</h1>
        <p className="mt-1 font-instrument text-sm text-textSecondary">
          Configuração de metas por área da operação.
        </p>
      </div>

      {loadError && <HabitaErrorNotice>{loadError}</HabitaErrorNotice>}
      {actionError && <HabitaErrorNotice>{actionError}</HabitaErrorNotice>}
      {successMessage && (
        <div className="rounded-lg border border-emerald-300/50 bg-emerald-500/10 px-4 py-3 font-instrument text-sm text-emerald-800 dark:text-emerald-200">
          {successMessage}
        </div>
      )}
      {!canEdit && (
        <div className="rounded-lg border border-amber-300/50 bg-amber-500/10 px-4 py-3 font-instrument text-sm text-amber-800 dark:text-amber-200">
          A configuração está em modo de consulta. Apenas Admin e Supervisor podem alterar metas.
        </div>
      )}

      <section className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
        <div className="flex flex-col gap-3 border-b border-border-subtle px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="font-fraunces text-base font-semibold text-textPrimary">Competência</h2>
            <p className="mt-1 font-instrument text-xs text-textSecondary">
              Defina o período e os parâmetros utilizados para distribuir as metas.
            </p>
          </div>
          <HabitaButton
            icon={copying ? 'ri-loader-4-line' : 'ri-file-copy-line'}
            size="sm"
            disabled={!canEdit || loading || copying}
            onClick={() => void copyPreviousMonth()}
          >
            {copying ? 'Copiando...' : 'Copiar mês anterior'}
          </HabitaButton>
        </div>

        <div className="grid gap-4 px-5 py-5 sm:grid-cols-3">
          <label className="space-y-1.5 font-instrument text-xs font-semibold text-textSecondary">
            <span>Mês</span>
            <HabitaSelect
              aria-label="Mês da competência"
              className="w-full"
              value={selectedMonth}
              disabled={loading}
              onChange={(event) => updateReferenceMonth(selectedYear, event.target.value)}
              options={monthLabels.map((label, index) => ({
                label,
                value: String(index + 1).padStart(2, '0'),
              }))}
            />
          </label>
          <label className="space-y-1.5 font-instrument text-xs font-semibold text-textSecondary">
            <span>Ano</span>
            <HabitaSelect
              aria-label="Ano da competência"
              className="w-full"
              value={selectedYear}
              disabled={loading}
              onChange={(event) => updateReferenceMonth(event.target.value, selectedMonth)}
              options={yearOptions}
            />
          </label>
          <label className="space-y-1.5 font-instrument text-xs font-semibold text-textSecondary">
            <span>Dias úteis</span>
            <HabitaTextInput
              aria-label="Dias úteis da competência"
              disabled={!canEdit || loading}
              inputMode="numeric"
              min={1}
              max={31}
              placeholder={String(countWeekdays(referenceMonth))}
              value={workingDays}
              onChange={(event) => {
                setWorkingDays(normalizeGoalInput(event.target.value));
                clearMessages();
              }}
            />
          </label>
        </div>
      </section>

      <section className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
        <div className="border-b border-border-subtle px-5 py-4">
          <h2 className="font-fraunces text-base font-semibold text-textPrimary">Metas do Time</h2>
          <p className="mt-1 font-instrument text-xs text-textSecondary">
            Habilite apenas os indicadores que serão utilizados nesta competência.
          </p>
        </div>

        {loading ? (
          <div className="space-y-3 p-5">
            {GOAL_TYPE_DEFINITIONS.map(({ type }) => (
              <div key={type} className="h-20 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : (
          <>
            <div className="divide-y divide-border-subtle px-5">
              {GOAL_TYPE_DEFINITIONS.map(({ label, type }) => {
                const enabled = enabledTypes.includes(type);
                return (
                  <div
                    key={type}
                    className={classNames(
                      'grid gap-4 py-4 transition-colors sm:grid-cols-[minmax(0,1fr)_220px] sm:items-center',
                      !enabled && 'text-textSecondary'
                    )}
                  >
                    <HabitaCheckbox
                      checked={enabled}
                      disabled={!canEdit}
                      className="p-0 hover:bg-transparent"
                      onCheckedChange={(checked) => toggleGoalType(type, checked)}
                    >
                      <span>
                        <span className="block text-sm font-semibold text-textPrimary">{label}</span>
                        <span className="mt-0.5 block text-xs text-textSecondary">
                          {enabled ? 'Indicador habilitado' : 'Indicador desabilitado'}
                        </span>
                      </span>
                    </HabitaCheckbox>
                    <label className="space-y-1.5 font-instrument text-xs font-semibold text-textSecondary">
                      <span>Meta do Time</span>
                      <HabitaTextInput
                        aria-label={`Meta do time para ${label}`}
                        className="text-right tabular-nums"
                        disabled={!canEdit || !enabled}
                        inputMode="numeric"
                        min={0}
                        placeholder="0"
                        value={teamGoals[type]}
                        onChange={(event) => {
                          const value = normalizeGoalInput(event.target.value);
                          setTeamGoals((current) => ({ ...current, [type]: value }));
                          clearMessages();
                        }}
                      />
                    </label>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col gap-3 border-t border-border-subtle px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <span className="font-instrument text-xs text-textSecondary">
                {competencyChanged ? 'Existem alterações pendentes' : 'Configuração atualizada'}
              </span>
              <HabitaButton
                icon={savingCompetency ? 'ri-loader-4-line' : 'ri-save-line'}
                variant="primary"
                disabled={!canEdit || savingCompetency || loading || !competencyChanged}
                onClick={() => void saveCompetency()}
              >
                {savingCompetency ? 'Salvando...' : 'Salvar competência'}
              </HabitaButton>
            </div>
          </>
        )}
      </section>

      <section className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
        <div className="border-b border-border-subtle px-5 py-4">
          <h2 className="font-fraunces text-base font-semibold text-textPrimary">Metas por SDR</h2>
          <p className="mt-1 font-instrument text-xs text-textSecondary">
            Defina a meta mensal de cada SDR para todos os indicadores habilitados.
          </p>
        </div>

        {loading ? (
          <div className="space-y-3 p-5">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex animate-pulse items-center gap-6">
                <div className="h-4 flex-1 rounded bg-muted" />
                <div className="h-9 w-32 rounded bg-muted" />
                <div className="h-9 w-32 rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : activeSdrs.length === 0 ? (
          <div className="p-5">
            <HabitaEmptyState
              icon="ri-user-search-line"
              title="Nenhum SDR ativo para esta competência"
              description="A lista é atualizada automaticamente a partir dos usuários ativos com perfil SDR."
              className="border-0"
            />
          </div>
        ) : enabledGoalDefinitions.length === 0 ? (
          <div className="px-5 py-10 text-center font-instrument">
            <i className="ri-equalizer-2-line text-3xl text-textSecondary/60" aria-hidden />
            <p className="mt-2 text-sm font-semibold text-textPrimary">Nenhuma meta habilitada</p>
            <p className="mt-1 text-xs text-textSecondary">
              Habilite ao menos um indicador em Metas do Time para configurar os SDRs.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table
                className="w-full border-collapse text-left font-instrument text-sm"
                style={{ minWidth: `${Math.max(720, 360 + enabledGoalDefinitions.length * 190)}px` }}
              >
                <thead className="bg-muted/50 text-xs text-textSecondary">
                  <tr>
                    <th className="min-w-[300px] px-5 py-3 font-semibold">SDR</th>
                    {enabledGoalDefinitions.map(({ label, type }) => (
                      <th key={type} className="w-[190px] px-5 py-3 text-right font-semibold">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {activeSdrs.map((user) => (
                    <tr key={user.id} className="transition-colors hover:bg-accent/40">
                      <td className="px-5 py-3">
                        <p className="font-semibold text-textPrimary">{user.name}</p>
                        <p className="text-xs text-textSecondary">{user.email}</p>
                      </td>
                      {enabledGoalDefinitions.map(({ label, type }) => (
                        <td key={type} className="px-5 py-3">
                          <HabitaTextInput
                            aria-label={`${label} de ${user.name}`}
                            className="text-right tabular-nums"
                            containerClassName="ml-auto w-32"
                            disabled={!canEdit}
                            inputMode="numeric"
                            min={0}
                            placeholder="0"
                            value={userGoals[user.id]?.[type] ?? ''}
                            onChange={(event) => {
                              const value = normalizeGoalInput(event.target.value);
                              setUserGoals((current) => ({
                                ...current,
                                [user.id]: {
                                  ...(current[user.id] ?? goalDraft()),
                                  [type]: value,
                                },
                              }));
                              clearMessages();
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 border-t border-border-subtle px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <span className="font-instrument text-xs text-textSecondary">
                {unavailableUserGoalChanges.length > 0
                  ? 'Salve a competência para habilitar os novos tipos de meta'
                  : clearedUserGoals.length > 0
                  ? 'Use 0 para zerar uma meta existente'
                  : userGoalChanges.length > 0
                    ? `${userGoalChanges.length} ${userGoalChanges.length === 1 ? 'meta alterada' : 'metas alteradas'}`
                    : 'Todas as metas estão atualizadas'}
              </span>
              <HabitaButton
                icon={savingUsers ? 'ri-loader-4-line' : 'ri-save-line'}
                variant="primary"
                disabled={
                  !canEdit ||
                  savingUsers ||
                  loading ||
                  userGoalChanges.length === 0 ||
                  clearedUserGoals.length > 0 ||
                  unavailableUserGoalChanges.length > 0
                }
                onClick={() => void saveUserGoals()}
              >
                {savingUsers ? 'Salvando...' : 'Salvar metas'}
              </HabitaButton>
            </div>
          </>
        )}
      </section>
    </section>
  );
}
